import { useEffect, useRef, useState } from 'react';
import type { DigitalMenuQr, DigitalMenuSettings, DigitalMenuSettingsPatch } from 'shared-models';
import { digitalMenuEmailSchema } from 'shared-models/browser';
import { DIGITAL_MENU_UPDATE, digitalMenuManagementService as api } from '../../../services/digital-menu/digital-menu-management.service';
import { downloadFile as download } from '../../../utils/download-file.util';

type Props = { opened: boolean; tenantId: string; employeeSession: string | null; admin?: boolean; onClose: () => void };
export function useDigitalMenuManagement({ opened, tenantId, employeeSession, admin = false, onClose }: Props) {
  const [settings, setSettings] = useState<DigitalMenuSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [displayName, setName] = useState('');
  const [details, setDetails] = useState('');
  const [featuredProductIds, setFeaturedIds] = useState<number[]>([]);
  const [recipient, setRecipient] = useState('');
  const [qr, setQr] = useState<DigitalMenuQr | null>(null);
  const [retry, setRetry] = useState(0);
  const lock = useRef(false);
  const active = useRef(false);
  const enabled = useRef(false);
  const dirty = useRef(false);
  const baseline = useRef<DigitalMenuSettings | null>(null);
  const revision = useRef(0);
  const lifecycle = useRef(0);

  useEffect(() => {
    if (!opened || (!admin && !employeeSession)) return;
    let cancelled = false;
    lifecycle.current++;
    active.current = true; dirty.current = false; enabled.current = false;
    setLoading(true); setError(''); setNotice(''); setSettings(null); setQr(null); setRecipient('');
    const apply = (incoming: DigitalMenuSettings) => {
      enabled.current = incoming.isDigitalMenu;
      setSettings(incoming);
      if (!incoming.isDigitalMenu) setQr(null);
      if (!dirty.current) { baseline.current = incoming; setName(incoming.displayName ?? ''); setDetails(incoming.details ?? ''); setFeaturedIds(incoming.featuredProductIds ?? []); }
    };
    const changed = (event: Event) => {
      const incoming = (event as CustomEvent<{ tenantId: string; settings: DigitalMenuSettings }>).detail;
      if (incoming?.tenantId !== tenantId) return;
      revision.current++; apply(incoming.settings);
    };
    window.addEventListener(DIGITAL_MENU_UPDATE, changed);
    const started = revision.current;
    const load = admin ? api.adminSettings(tenantId) : api.settings(tenantId, employeeSession!);
    load.then((incoming) => { if (!cancelled && started === revision.current) apply(incoming); })
      .catch(() => { if (!cancelled) setError('טעינת הגדרות התפריט נכשלה'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; active.current = false; lifecycle.current++; window.removeEventListener(DIGITAL_MENU_UPDATE, changed); };
  }, [opened, tenantId, employeeSession, admin, retry]);

  const run = async (operation: string, action: (isCurrent: () => boolean) => Promise<void>, requiresEnabled = true) => {
    if (!active.current || lock.current || (requiresEnabled && !enabled.current)) return;
    lock.current = true; setBusy(operation); setError(''); setNotice('');
    const started = lifecycle.current;
    const isCurrent = () => active.current && lifecycle.current === started;
    try { await action(isCurrent); }
    catch { if (isCurrent()) setError('הפעולה נכשלה. נסו שוב.'); }
    finally { lock.current = false; if (active.current) setBusy(''); }
  };
  const save = () => {
    if (!baseline.current || !enabled.current) return;
    const patch: DigitalMenuSettingsPatch = {};
    const name = displayName.trim() || null; const text = details.trim() || null;
    if (name !== baseline.current.displayName) patch.displayName = name;
    if (text !== baseline.current.details) patch.details = text;
    if (JSON.stringify(featuredProductIds) !== JSON.stringify(baseline.current.featuredProductIds ?? [])) patch.featuredProductIds = featuredProductIds;
    if (!Object.keys(patch).length) { onClose(); return; }
    return run('save', async (isCurrent) => { await api.save(tenantId, employeeSession!, patch); if (isCurrent()) onClose(); });
  };
  const generate = () => run('qr', async (isCurrent) => { const incoming = await api.qr(tenantId, employeeSession!); if (isCurrent() && enabled.current) setQr(incoming); });
  const sendEmail = () => {
    const parsed = digitalMenuEmailSchema.safeParse({ email: recipient });
    if (!parsed.success) { setError('יש להזין כתובת מייל תקינה'); return; }
    return run('email', async (isCurrent) => { await api.email(tenantId, employeeSession!, parsed.data.email); if (isCurrent()) setNotice('המייל נשלח'); });
  };
  const image = (file: File | null) => {
    if (file && (file.size > 3 * 1024 * 1024 || !['image/jpeg', 'image/png', 'image/webp'].includes(file.type))) { setError('יש לבחור תמונת JPEG, PNG או WebP עד 3MB'); return; }
    return run('image', () => api.image(tenantId, employeeSession!, file));
  };
  const pdf = () => run('pdf', async (isCurrent) => {
    const blob = await api.pdf(tenantId, employeeSession!);
    if (!isCurrent() || !enabled.current) return;
    const url = URL.createObjectURL(blob);
    download(url, 'digital-menu-qr.pdf'); window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
  return { settings, loading, busy, error, notice, displayName, details, featuredProductIds, recipient, setRecipient, qr, save, generate, sendEmail, image, pdf,
    retry: () => setRetry((value) => value + 1),
    setDisplayName: (value: string) => { dirty.current = true; setName(value); },
    setDetails: (value: string) => { dirty.current = true; setDetails(value); },
    setFeaturedProductIds: (ids: number[]) => { if (ids.length <= 6 && new Set(ids).size === ids.length) { dirty.current = true; setFeaturedIds(ids); } },
    entitlement: (value: boolean) => run('entitlement', () => api.entitlement(tenantId, value), false),
    downloadPng: () => { if (qr && enabled.current) download(qr.qrDataUrl, 'digital-menu-qr.png'); },
    copy: () => run('copy', async (isCurrent) => { if (qr) { await navigator.clipboard.writeText(qr.menuUrl); if (isCurrent()) setNotice('הקישור הועתק'); } }),
  };
}